import { Suspense } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { Helmet } from '@xing-com/crate-helmet';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { FullPageDotLoading } from '@xing-com/crate-jobs-components-full-page-dot-loading';
import { LoadingError } from '@xing-com/crate-jobs-domain-apply-components';
import {
  useGetUserNeffiScore,
  useGetUserRecentCvs,
  useUserEmailWithPhoneFallback,
} from '@xing-com/crate-jobs-domain-apply-hooks';
import type { InstantApplyJobDataType } from '@xing-com/crate-jobs-domain-apply-types';
import {
  InstantApplyLoggedIn,
  InstantApplyLoggedOut,
} from '@xing-com/crate-jobs-domain-apply-xinglets';
import type { Xinglet } from '@xing-com/crate-xinglet';

import { useInstantApplyJobData } from './hooks/use-instant-apply-job-data';

export default class InstantApply implements Xinglet {
  public getComponent() {
    const InstantApplyContent: React.FC = () => {
      const intl = useIntl();
      const { isLoggedIn } = useLoginState();
      const { isJobDataError, isLoading, job, refetch } =
        useInstantApplyJobData();
      const { isNeffiScoreLoading, normalizedNeffiScore, neffiStatus } =
        useGetUserNeffiScore(isLoggedIn);
      const { isUserEmailLoading, userFormData } =
        useUserEmailWithPhoneFallback(isLoggedIn);
      const {
        isGetUserRecentCvsLoading,
        isGetUserRecentCvsError,
        userRecentCvs,
      } = useGetUserRecentCvs(isLoggedIn);

      const jobTitle = job?.__typename === 'VisibleJob' ? job.title : '';

      const isPageLoading =
        isLoading ||
        isNeffiScoreLoading ||
        isUserEmailLoading ||
        isGetUserRecentCvsLoading;

      const isPageError = isJobDataError;

      return (
        <>
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          {isPageLoading ? (
            <FullPageDotLoading />
          ) : isPageError ? (
            <LoadingError
              refetch={() => {
                refetch();
              }}
            />
          ) : (
            <>
              {jobTitle ? (
                <Helmet>
                  <title>
                    {intl.formatMessage(
                      { id: 'JOBS_INSTANT_APPLY_BROWSER_TAB_TITLE' },
                      { job_title: jobTitle }
                    )}
                  </title>
                </Helmet>
              ) : null}
              {isLoggedIn ? (
                <InstantApplyLoggedIn
                  jobData={job as InstantApplyJobDataType}
                  neffiScoreData={{ normalizedNeffiScore, neffiStatus }}
                  userFormData={userFormData}
                  userRecentCvData={{ userRecentCvs, isGetUserRecentCvsError }}
                />
              ) : (
                <InstantApplyLoggedOut job={job as InstantApplyJobDataType} />
              )}
            </>
          )}
        </>
      );
    };

    return () => (
      <ErrorBoundary
        onError={() => (
          <>
            <Helmet>
              <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <LoadingError
              refetch={() => {
                window.location.reload();
              }}
            />
          </>
        )}
      >
        <Suspense
          fallback={
            <>
              <Helmet>
                <meta name="robots" content="noindex, nofollow" />
              </Helmet>
              <FullPageDotLoading />
            </>
          }
        >
          <InstantApplyContent />
        </Suspense>
      </ErrorBoundary>
    );
  }
}
